import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { formatMoney, isUS, stateLabel } from './countryFormat';

// ---------------------------------------------------------------------------
// Helpers (local to modal — mirrors DealStructureResults helpers)
// ---------------------------------------------------------------------------

// The result's currency (CAD for Canadian owners, USD for US owners), set at
// the top of each render before anything is formatted.
let displayCurrency = 'CAD';

function formatCurrency(value) {
  return formatMoney(value, displayCurrency);
}

function formatPercent(value) {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

function StatusDot({ variant = 'orange' }) {
  const colors = { orange: 'hsl(var(--succeedo-orange))', green: 'hsl(var(--chart-2))' };
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ backgroundColor: colors[variant] || colors.orange }}
    />
  );
}

// ---------------------------------------------------------------------------
// CashFlowProjectionModal
// ---------------------------------------------------------------------------

export default function CashFlowProjectionModal({
  result,
  inputs,
  onClose,
  hasBuyerFinancing = false,
  buyerFinancingData = null,
}) {
  const [tab, setTab] = useState('seller');

  displayCurrency = result?.currency || 'CAD';
  if (!result) return null;

  const {
    seller_cash_flows: sellerFlows = [],
    buyer_cash_flows: buyerFlows = [],
    tax,
    summary,
    consulting: resultConsulting,
    seller_note: resultNote,
  } = result;

  // Province label
  const us = isUS(tax?.country);
  const provinceLabel = us
    ? stateLabel(tax?.region) || 'Not set'
    : tax?.province || inputs?.province || 'ON';
  const marginalRate = tax?.marginal_rate || 0;

  // -----------------------------------------------------------------------
  // Seller tab data
  // -----------------------------------------------------------------------
  const sellerTotals = useMemo(() => {
    return sellerFlows.reduce(
      (acc, row) => ({
        cash_received: acc.cash_received + (row.cash_received || 0),
        seller_note_principal: acc.seller_note_principal + (row.seller_note_principal || 0),
        seller_note_interest: acc.seller_note_interest + (row.seller_note_interest || 0),
        consulting_income: acc.consulting_income + (row.consulting_income || 0),
        earnout_income: acc.earnout_income + (row.earnout_income || 0),
        gross_income: acc.gross_income + (row.gross_income || 0),
        tax: acc.tax + (row.tax || 0),
        net_income: acc.net_income + (row.net_income || 0),
      }),
      {
        cash_received: 0,
        seller_note_principal: 0,
        seller_note_interest: 0,
        consulting_income: 0,
        earnout_income: 0,
        gross_income: 0,
        tax: 0,
        net_income: 0,
      }
    );
  }, [sellerFlows]);

  // Determine which seller columns to show (hide all-zero columns)
  const showSellerNote = sellerTotals.seller_note_principal > 0;
  const showSellerInterest = sellerTotals.seller_note_interest > 0;
  const showSellerConsulting = sellerTotals.consulting_income > 0;
  const showSellerEarnout = sellerTotals.earnout_income > 0;

  // -----------------------------------------------------------------------
  // Buyer tab data
  // -----------------------------------------------------------------------
  const buyerTotals = useMemo(() => {
    return buyerFlows.reduce(
      (acc, row) => ({
        down_payment: acc.down_payment + (row.down_payment || 0),
        seller_note_payment: acc.seller_note_payment + (row.seller_note_payment || 0),
        seller_note_interest: acc.seller_note_interest + (row.seller_note_interest || 0),
        earnout_payment: acc.earnout_payment + (row.earnout_payment || 0),
        consulting_fee: acc.consulting_fee + (row.consulting_fee || 0),
        total_annual_cost: acc.total_annual_cost + (row.total_annual_cost || 0),
      }),
      {
        down_payment: 0,
        seller_note_payment: 0,
        seller_note_interest: 0,
        earnout_payment: 0,
        consulting_fee: 0,
        total_annual_cost: 0,
      }
    );
  }, [buyerFlows]);

  const showBuyerDownPayment = buyerTotals.down_payment > 0;
  const showBuyerNote = buyerTotals.seller_note_payment > 0;
  const showBuyerNoteInterest = buyerTotals.seller_note_interest > 0;
  const showBuyerEarnout = buyerTotals.earnout_payment > 0;
  const showBuyerConsulting = buyerTotals.consulting_fee > 0;

  // Check if buyer flows include business projection data (revenue/expenses/ebitda)
  const hasBusinessProjections = buyerFlows.some(
    (row) => row.revenue != null && row.revenue > 0
  );

  // Buyer financing data (client-side, passed as props)
  const bf = buyerFinancingData;
  const showBuyerFinancing = hasBuyerFinancing && bf;

  // Structure label for display
  const structureLabel = (result.structure_type || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Max years for buyer (including buyer financing term if present)
  const buyerMaxYears = buyerFlows.length > 1 ? buyerFlows.length - 1 : 0;

  // Seller effective tax rate
  const sellerEffectiveTax =
    sellerTotals.gross_income > 0 ? sellerTotals.tax / sellerTotals.gross_income : 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[16px] p-8 max-w-[900px] w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Heading */}
        <h2 className="font-serif text-[28px] font-light text-foreground mb-6">
          Deal Cash Flow Projections
        </h2>

        {/* Tab Toggle */}
        <div className="flex gap-3 mb-8">
          <button
            type="button"
            onClick={() => setTab('seller')}
            className={`px-4 py-2 font-mono text-[11px] uppercase rounded-full transition-colors ${
              tab === 'seller'
                ? 'bg-foreground text-background'
                : 'border border-border text-muted-foreground hover:border-foreground/30'
            }`}
          >
            SELLER VIEW
          </button>
          <button
            type="button"
            onClick={() => setTab('buyer')}
            className={`px-4 py-2 font-mono text-[11px] uppercase rounded-full transition-colors ${
              tab === 'buyer'
                ? 'bg-foreground text-background'
                : 'border border-border text-muted-foreground hover:border-foreground/30'
            }`}
          >
            BUYER VIEW
          </button>
        </div>

        {/* ============================================================ */}
        {/* SELLER VIEW                                                   */}
        {/* ============================================================ */}
        {tab === 'seller' && (
          <div className="space-y-8">
            {/* Deal Summary */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  DEAL SUMMARY
                </span>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    Asking Price
                  </div>
                  <div className="text-[14px] font-medium text-foreground">
                    {formatCurrency(result.asking_price)}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    Structure
                  </div>
                  <div className="text-[14px] font-medium text-foreground">{structureLabel}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    {us ? 'State' : 'Province'}
                  </div>
                  <div className="text-[14px] font-medium text-foreground">{provinceLabel}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    Marginal Rate
                  </div>
                  <div className="text-[14px] font-medium text-foreground">
                    {formatPercent(marginalRate)}
                  </div>
                </div>
              </div>
            </div>

            {/* Seller Cash Flow Table */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  SELLER ANNUAL CASH FLOWS
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-muted">
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-left">
                        Year
                      </th>
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                        Cash Received
                      </th>
                      {showSellerNote && (
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Seller Note
                        </th>
                      )}
                      {showSellerInterest && (
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Interest
                        </th>
                      )}
                      {showSellerConsulting && (
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Consulting
                        </th>
                      )}
                      {showSellerEarnout && (
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Earnout
                        </th>
                      )}
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                        Gross Income
                      </th>
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                        Tax
                      </th>
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                        Net Income
                      </th>
                      <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                        Cumulative
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellerFlows.map((row, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? '' : 'bg-muted/30'}
                      >
                        <td className="px-3 py-2 text-foreground">{row.period}</td>
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(row.cash_received)}
                        </td>
                        {showSellerNote && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(row.seller_note_principal)}
                          </td>
                        )}
                        {showSellerInterest && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(row.seller_note_interest)}
                          </td>
                        )}
                        {showSellerConsulting && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(row.consulting_income)}
                          </td>
                        )}
                        {showSellerEarnout && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(row.earnout_income)}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(row.gross_income)}
                        </td>
                        <td className="px-3 py-2 text-right text-destructive">
                          {formatCurrency(row.tax)}
                        </td>
                        <td className="px-3 py-2 text-right text-[hsl(var(--chart-2))]">
                          {formatCurrency(row.net_income)}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(row.cumulative || row.net_income)}
                        </td>
                      </tr>
                    ))}
                    {/* Summary Row */}
                    <tr className="border-t-2 border-foreground font-semibold">
                      <td className="px-3 py-2 text-foreground">TOTAL</td>
                      <td className="px-3 py-2 text-right text-foreground">
                        {formatCurrency(sellerTotals.cash_received)}
                      </td>
                      {showSellerNote && (
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(sellerTotals.seller_note_principal)}
                        </td>
                      )}
                      {showSellerInterest && (
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(sellerTotals.seller_note_interest)}
                        </td>
                      )}
                      {showSellerConsulting && (
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(sellerTotals.consulting_income)}
                        </td>
                      )}
                      {showSellerEarnout && (
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(sellerTotals.earnout_income)}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right text-foreground">
                        {formatCurrency(sellerTotals.gross_income)}
                      </td>
                      <td className="px-3 py-2 text-right text-destructive">
                        {formatCurrency(sellerTotals.tax)}
                      </td>
                      <td className="px-3 py-2 text-right text-[hsl(var(--chart-2))]">
                        {formatCurrency(sellerTotals.net_income)}
                      </td>
                      <td className="px-3 py-2 text-right text-foreground">
                        {formatCurrency(
                          sellerFlows.length > 0
                            ? sellerFlows[sellerFlows.length - 1].cumulative ||
                                sellerTotals.net_income
                            : sellerTotals.net_income
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Key Insights */}
              <div className="bg-muted rounded-[12px] p-4 mt-4 space-y-2">
                <div className="text-[13px] text-foreground">
                  Total gross proceeds: {formatCurrency(sellerTotals.gross_income)}
                </div>
                <div className="text-[13px] text-foreground">
                  Total tax burden: {formatCurrency(sellerTotals.tax)} (
                  {formatPercent(sellerEffectiveTax)} effective)
                </div>
                <div className="text-[13px] font-medium text-[hsl(var(--chart-2))]">
                  Net to seller: {formatCurrency(sellerTotals.net_income)}
                </div>
                {showSellerConsulting && (
                  <div className="text-[13px] text-foreground">
                    Consulting contributes{' '}
                    {formatCurrency(
                      sellerTotals.consulting_income -
                        sellerTotals.consulting_income * marginalRate
                    )}{' '}
                    net after tax
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* BUYER VIEW                                                    */}
        {/* ============================================================ */}
        {tab === 'buyer' && (
          <div className="space-y-8">
            {/* Deal Summary */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  DEAL SUMMARY
                </span>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    Purchase Price
                  </div>
                  <div className="text-[14px] font-medium text-foreground">
                    {formatCurrency(result.asking_price)}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    Down Payment
                  </div>
                  <div className="text-[14px] font-medium text-foreground">
                    {formatCurrency(result.cash_at_closing)}
                  </div>
                </div>
                {showBuyerFinancing && (
                  <>
                    <div>
                      <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                        Financing Amount
                      </div>
                      <div className="text-[14px] font-medium text-foreground">
                        {formatCurrency(bf.loanAmount)}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                        Monthly Debt Service
                      </div>
                      <div className="text-[14px] font-medium text-foreground">
                        {formatCurrency(bf.monthlyPayment)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Buyer Cash Flow Table */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  BUYER ANNUAL CASH FLOWS
                </span>
              </div>
              <div className="overflow-x-auto">
                {hasBusinessProjections ? (
                  /* Enriched buyer table with revenue/expense projections */
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-muted">
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-left">
                          Year
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Revenue
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Expenses
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          EBITDA
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Debt Service
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Net Cash Flow
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Cumulative
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyerFlows.map((row, index) => {
                        const isClosing = row.period === 'Closing';
                        return (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? '' : 'bg-muted/30'}
                          >
                            <td className="px-3 py-2 text-foreground">{row.period}</td>
                            <td className="px-3 py-2 text-right text-foreground">
                              {isClosing ? '\u2014' : formatCurrency(row.revenue)}
                            </td>
                            <td className="px-3 py-2 text-right text-foreground">
                              {isClosing ? '\u2014' : formatCurrency(row.expenses)}
                            </td>
                            <td className="px-3 py-2 text-right text-foreground font-medium">
                              {isClosing ? '\u2014' : formatCurrency(row.ebitda)}
                            </td>
                            <td className="px-3 py-2 text-right text-foreground">
                              {isClosing
                                ? formatCurrency(row.down_payment)
                                : formatCurrency(row.debt_service)}
                            </td>
                            <td className={`px-3 py-2 text-right font-medium ${
                              isClosing
                                ? 'text-foreground'
                                : (row.net_cash_flow || 0) >= 0
                                ? 'text-[hsl(var(--chart-2))]'
                                : 'text-destructive'
                            }`}>
                              {isClosing
                                ? formatCurrency(-row.down_payment)
                                : formatCurrency(row.net_cash_flow)}
                            </td>
                            <td className="px-3 py-2 text-right text-foreground font-medium">
                              {isClosing
                                ? formatCurrency(-row.down_payment)
                                : formatCurrency(row.cumulative_net_cash_flow)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  /* Standard buyer table (no revenue data) */
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-muted">
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-left">
                          Year
                        </th>
                        {showBuyerDownPayment && (
                          <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                            Down Payment
                          </th>
                        )}
                        {showBuyerNote && (
                          <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                            Seller Note
                          </th>
                        )}
                        {showBuyerNoteInterest && (
                          <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                            Note Interest
                          </th>
                        )}
                        {showBuyerEarnout && (
                          <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                            Earnout
                          </th>
                        )}
                        {showBuyerConsulting && (
                          <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                            Consulting Fee
                          </th>
                        )}
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Total Annual Cost
                        </th>
                        <th className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right">
                          Cumulative Paid
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyerFlows.map((row, index) => (
                        <tr
                          key={index}
                          className={index % 2 === 0 ? '' : 'bg-muted/30'}
                        >
                          <td className="px-3 py-2 text-foreground">{row.period}</td>
                          {showBuyerDownPayment && (
                            <td className="px-3 py-2 text-right text-foreground">
                              {formatCurrency(row.down_payment)}
                            </td>
                          )}
                          {showBuyerNote && (
                            <td className="px-3 py-2 text-right text-foreground">
                              {formatCurrency(row.seller_note_payment)}
                            </td>
                          )}
                          {showBuyerNoteInterest && (
                            <td className="px-3 py-2 text-right text-foreground">
                              {formatCurrency(row.seller_note_interest)}
                            </td>
                          )}
                          {showBuyerEarnout && (
                            <td className="px-3 py-2 text-right text-foreground">
                              {formatCurrency(row.earnout_payment)}
                            </td>
                          )}
                          {showBuyerConsulting && (
                            <td className="px-3 py-2 text-right text-foreground">
                              {formatCurrency(row.consulting_fee)}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(row.total_annual_cost)}
                          </td>
                          <td className="px-3 py-2 text-right text-foreground font-medium">
                            {formatCurrency(row.cumulative_paid || row.total_annual_cost)}
                          </td>
                        </tr>
                      ))}
                      {/* Summary Row */}
                      <tr className="border-t-2 border-foreground font-semibold">
                        <td className="px-3 py-2 text-foreground">TOTAL</td>
                        {showBuyerDownPayment && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(buyerTotals.down_payment)}
                          </td>
                        )}
                        {showBuyerNote && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(buyerTotals.seller_note_payment)}
                          </td>
                        )}
                        {showBuyerNoteInterest && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(buyerTotals.seller_note_interest)}
                          </td>
                        )}
                        {showBuyerEarnout && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(buyerTotals.earnout_payment)}
                          </td>
                        )}
                        {showBuyerConsulting && (
                          <td className="px-3 py-2 text-right text-foreground">
                            {formatCurrency(buyerTotals.consulting_fee)}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right text-foreground">
                          {formatCurrency(buyerTotals.total_annual_cost)}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground font-medium">
                          {formatCurrency(
                            buyerFlows.length > 0
                              ? buyerFlows[buyerFlows.length - 1].cumulative_paid ||
                                  buyerTotals.total_annual_cost
                              : buyerTotals.total_annual_cost
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {/* Key Insights */}
              <div className="bg-muted rounded-[12px] p-4 mt-4 space-y-2">
                <div className="text-[13px] text-foreground">
                  Total cost of acquisition: {formatCurrency(buyerTotals.total_annual_cost)}
                </div>
                {hasBusinessProjections && buyerFlows.length > 1 && (
                  <div className="text-[13px] text-[hsl(var(--chart-2))]">
                    Cumulative net cash flow after {buyerMaxYears} years:{' '}
                    {formatCurrency(
                      buyerFlows[buyerFlows.length - 1].cumulative_net_cash_flow
                    )}
                  </div>
                )}
                <div className="text-[13px] text-foreground">
                  Of which: Principal{' '}
                  {formatCurrency(
                    buyerTotals.down_payment + buyerTotals.seller_note_payment
                  )}{' '}
                  + Interest {formatCurrency(buyerTotals.seller_note_interest)} + Consulting{' '}
                  {formatCurrency(buyerTotals.consulting_fee)}
                </div>
                {buyerMaxYears > 0 && (
                  <div className="text-[13px] text-foreground">
                    Effective annual cost of ownership:{' '}
                    {formatCurrency(buyerTotals.total_annual_cost / buyerMaxYears)}/yr
                  </div>
                )}
                {showBuyerFinancing && bf && (
                  <div className="text-[13px] text-foreground">
                    Total cost of borrowing: {formatCurrency(bf.totalInterest)} (interest on
                    acquisition loan)
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-border space-y-4">
          <p className="text-[12px] text-muted-foreground">
            {us
              ? 'Projections are estimates based on current inputs. Tax calculations use current US federal and state rates. Installment-sale treatment and timing should be confirmed with your accountant.'
              : 'Projections are estimates based on current inputs. Tax calculations use 2026 Canadian rates. Capital Gains Reserve eligibility and timing should be confirmed with your accountant.'}
          </p>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onClose}
              className="bg-foreground text-background rounded-full px-8 py-2.5 text-[14px] hover:bg-foreground/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
